/**
 * CMS Medicare Physician Fee Schedule — Global Periods
 * ───────────────────────────────────────────────────────────
 * Maps CPT / HCPCS codes to their CMS global-surgery indicator:
 *   000 — Endoscopic or minor procedure (post-op period = 0 days)
 *   010 — Minor surgery (10-day post-op period)
 *   090 — Major surgery (90-day post-op period)
 *   XXX — Global concept does not apply
 *   YYY — Contractor-priced (varies)
 *   ZZZ — Add-on code (global of primary applies)
 *   MMM — Maternity codes (special rule)
 *
 * Dataset embedded here is a curated subset of the most common
 * outpatient surgical codes (~50 entries). For the full CMS
 * table, substitute the annual PFSRVU `GLOB` column lookup.
 *
 * Source references: CMS Physician Fee Schedule 2024 PFSRVU
 * file; AMA CPT descriptions trimmed for demo.
 */

export const GLOBAL_DAYS = {
  '000': 0,  '010': 10, '090': 90,
  'XXX': null, 'YYY': null, 'ZZZ': null, 'MMM': null,
};

/* `period` uses the raw CMS indicator; `days` is convenience. */
const entry = (code, period, desc, specialty) => ({ code, period, days: GLOBAL_DAYS[period], desc, specialty });

export const CMS_GLOBAL_PERIODS = [
  /* Orthopedics */
  entry('27447', '090', 'Total knee arthroplasty', 'Ortho'),
  entry('27130', '090', 'Total hip arthroplasty',  'Ortho'),
  entry('27446', '090', 'Unicompartmental knee arthroplasty', 'Ortho'),
  entry('29881', '090', 'Knee arthroscopy w/ meniscectomy', 'Ortho'),
  entry('29827', '090', 'Shoulder arthroscopy, rotator cuff repair', 'Ortho'),
  entry('29826', '090', 'Shoulder arthroscopy, SAD + CA release', 'Ortho'),
  entry('64721', '090', 'Carpal tunnel release, open', 'Ortho'),
  entry('26055', '010', 'Trigger finger release', 'Ortho'),
  entry('28285', '090', 'Hammertoe correction', 'Ortho'),

  /* General surgery */
  entry('47562', '090', 'Laparoscopic cholecystectomy', 'Surgery'),
  entry('47600', '090', 'Open cholecystectomy', 'Surgery'),
  entry('44970', '090', 'Laparoscopic appendectomy', 'Surgery'),
  entry('49505', '090', 'Inguinal hernia repair, age ≥5', 'Surgery'),
  entry('49650', '090', 'Laparoscopic inguinal hernia repair', 'Surgery'),
  entry('44204', '090', 'Laparoscopic partial colectomy', 'Surgery'),
  entry('19120', '090', 'Breast lumpectomy (excision benign lesion)', 'Surgery'),
  entry('19301', '090', 'Partial mastectomy', 'Surgery'),
  entry('11042', '000', 'Wound debridement, subQ tissue, first 20 cm²', 'Surgery'),

  /* OB/GYN */
  entry('58150', '090', 'Total abdominal hysterectomy', 'OB/GYN'),
  entry('58570', '090', 'Total laparoscopic hysterectomy', 'OB/GYN'),
  entry('58670', '090', 'Laparoscopic tubal ligation with Falope rings', 'OB/GYN'),
  entry('58120', '010', 'Dilation and curettage', 'OB/GYN'),
  entry('58100', '000', 'Endometrial biopsy', 'OB/GYN'),
  entry('59400', 'MMM', 'Routine OB care — global (antepartum + delivery + postpartum)', 'OB/GYN'),
  entry('59510', 'MMM', 'Routine OB care — cesarean delivery', 'OB/GYN'),

  /* Urology */
  entry('55866', '090', 'Robotic-assisted radical prostatectomy', 'Uro'),
  entry('52601', '090', 'Transurethral resection of prostate (TURP)', 'Uro'),
  entry('52000', '000', 'Cystourethroscopy', 'Uro'),
  entry('50590', '090', 'Lithotripsy, extracorporeal shock wave', 'Uro'),
  entry('54150', '010', 'Circumcision with clamp (newborn)', 'Uro'),

  /* ENT / Head & Neck */
  entry('42820', '090', 'Tonsillectomy + adenoidectomy, <12y', 'ENT'),
  entry('42826', '090', 'Tonsillectomy, ≥12y', 'ENT'),
  entry('31231', '000', 'Nasal endoscopy, diagnostic', 'ENT'),
  entry('60240', '090', 'Total thyroidectomy', 'ENT'),
  entry('69436', '010', 'Tympanostomy with tubes, child', 'ENT'),

  /* Dermatology */
  entry('11102', '000', 'Tangential biopsy of skin, single', 'Derm'),
  entry('11402', '010', 'Excision, benign skin lesion, trunk/arms/legs 1.1–2.0cm', 'Derm'),
  entry('11602', '010', 'Excision, malignant lesion, trunk/arms/legs 1.1–2.0cm', 'Derm'),
  entry('17000', '010', 'Destruction, premalignant lesion, first', 'Derm'),
  entry('12001', '000', 'Simple repair, scalp/neck/axillae/genitalia 2.5cm or less', 'Derm'),

  /* GI */
  entry('45378', '000', 'Colonoscopy, diagnostic', 'GI'),
  entry('45380', '000', 'Colonoscopy w/ biopsy', 'GI'),
  entry('43239', '000', 'EGD with biopsy', 'GI'),
  entry('45330', '000', 'Flexible sigmoidoscopy', 'GI'),

  /* Ophthalmology */
  entry('66984', '090', 'Cataract extraction w/ IOL', 'Ophth'),
  entry('65855', '010', 'Laser trabeculoplasty', 'Ophth'),
  entry('67028', '000', 'Intravitreal injection', 'Ophth'),

  /* Pain management */
  entry('64483', '000', 'Transforaminal epidural steroid injection, lumbar', 'Pain'),
  entry('64490', '000', 'Paravertebral facet joint injection, cervical', 'Pain'),
  entry('62321', '000', 'Cervical/thoracic epidural w/ imaging', 'Pain'),

  /* Cardiology procedures */
  entry('93458', '000', 'Left heart catheterization, coronary angiography', 'Cards'),
  entry('93000', '000', 'Electrocardiogram (ECG) — routine', 'Cards'),
  entry('33533', '090', 'CABG, single arterial graft', 'Cards'),
];

const INDEX = (() => {
  const m = new Map();
  for (const e of CMS_GLOBAL_PERIODS) m.set(e.code, e);
  return m;
})();

/**
 * Look up global-period info for a CPT code.
 * Returns null if code is not in the curated table.
 */
export const lookupGlobalPeriod = (cpt) => INDEX.get(String(cpt).trim()) || null;

/* Helper — is the encounter still inside the post-op global period?
     surgeryDateISO: 'YYYY-MM-DD', cpt: '27447'
*/
export const globalPeriodStatus = (surgeryDateISO, cpt) => {
  const rec = lookupGlobalPeriod(cpt);
  if (!rec || rec.days == null) return { active: false, daysRemaining: 0, rec };
  const elapsed = Math.floor((Date.now() - new Date(surgeryDateISO).getTime()) / 86400000);
  const daysRemaining = Math.max(0, rec.days - elapsed);
  return { active: daysRemaining > 0, daysRemaining, elapsed, rec };
};

export default lookupGlobalPeriod;
